import { createSelector } from "@reduxjs/toolkit";

import {
  PaneContentType,
  PollResponseType,
  PollSessionStatusType,
  PollSessionType,
  PollType,
  RootState,
} from "shared/types";

export const selectPollById = (id: string | undefined) => (state: RootState) =>
  id ? state.classroom.client.polls[id] : undefined;

export const selectPolls = () => (state: RootState) => state.classroom.client.polls;

export const selectPollResponseById = (id: string) => (state: RootState) =>
  state.classroom.client.poll_responses[id];

const _selectPollResponses = (state: RootState) => state.classroom.client.poll_responses;

export const selectPollResponses = () => (state: RootState) =>
  state.classroom.client.poll_responses;

export const selectPollResponsesByPollSessionId = (pollSessionId: string | undefined) =>
  createSelector([_selectPollResponses, () => pollSessionId], (pollResponses, _pollSessionId) =>
    Object.values(pollResponses).filter(
      (pollResponse) => pollResponse.poll_session_id === _pollSessionId,
    ),
  );

const _selectPollResponsesByUserId = (userId: string) =>
  createSelector([_selectPollResponses, () => userId], (pollResponses, _userId) =>
    Object.values(pollResponses).filter((pollResponse) => pollResponse.user_id === _userId),
  );

export const selectPollResponsesByPollSessionIdAndUserId = (
  pollSessionId: string,
  userId: string,
) =>
  createSelector(
    [_selectPollResponses, () => pollSessionId, () => userId],
    (pollResponses, _pollSessionId, _userId) =>
      Object.values(pollResponses).filter(
        (pollResponse) =>
          pollResponse.poll_session_id === _pollSessionId && pollResponse.user_id === _userId,
      ),
  );

type ExtendedPollResponseType = PollResponseType & {
  poll: PollType;
  poll_session: PollSessionType;
};

export const selectPollResponsesForPollResponsesIndicator = (
  userId: string,
  canControlClassroom: boolean,
) =>
  createSelector(
    [
      () => canControlClassroom,
      _selectPollSessionsForActivePollSessionPanes(),
      _selectPollResponsesByUserId(userId),
      selectPolls(),
    ],
    (_canControlClassroom, pollSessions, pollResponses, polls) => {
      const pollResponsesForBar: ExtendedPollResponseType[] = [];
      const pollResponsesForDropdown: ExtendedPollResponseType[] = [];
      pollSessions.forEach((pollSession) => {
        const poll = polls[pollSession.poll_id];
        const pollResponse = pollResponses.find(
          (_pollResponse) =>
            _pollResponse.poll_session_id === pollSession.id && !_pollResponse.is_draft,
        );
        if (pollResponse) {
          pollResponsesForBar.push({ ...pollResponse, poll, poll_session: pollSession });
        }
        if (
          pollResponse &&
          (_canControlClassroom ||
            (pollSession.status === PollSessionStatusType.Completed && poll.student_visible))
        ) {
          pollResponsesForDropdown.push({ ...pollResponse, poll, poll_session: pollSession });
        }
      });
      return [pollResponsesForBar, pollResponsesForDropdown];
    },
  );

export const selectPollSessions = () => (state: RootState) => state.classroom.client.poll_sessions;

export const selectPollSessionById = (id: string | undefined) => (state: RootState) =>
  id ? state.classroom.client.poll_sessions[id] : undefined;

export const selectPollSessionsByPollId = (pollId: string | undefined) => (state: RootState) =>
  Object.values(state.classroom.client.poll_sessions).filter(
    (pollSession) => pollSession.poll_id === pollId,
  );

const _selectPollSessionsForActivePollSessionPanes = () => (state: RootState) => {
  const pollSessionPanes = state.classroom.client.stage.panes.filter(
    (pane) => pane && pane.content_type === PaneContentType.PollSession,
  );
  return pollSessionPanes
    .map((pane) => pane.content_id)
    .map((id) => state.classroom.client.poll_sessions[id as string]);
};
